import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from 'reactflow';
import type { ArtifactTileData } from '../types';
import { Shimmer, ContentLoadFailed, RawTextDump, ArtifactTileErrorBoundary } from './ArtifactTileExpandedChrome';
import { TILE_FIT_DURATION, TILE_FIT_PADDING, TILE_FIT_MAX_ZOOM, expandedTileSize } from '../chatDagTokens';

/**
 * Artifact tile — Trailmap L1 (plan Task 12) ▣ node.
 *
 * Per spec §8.7 a11y: role="button", aria-label cites artifact name +
 * type and "Press Enter to open." Both Enter and Space activate.
 * Dark-mode parity per feedback_dark_mode_parity_first_class.
 *
 * L2 Task 0.4: mouse clicks are routed at the canvas level via
 * ReactFlow's onNodeClick (reading `node.data.artifact.id`). The
 * keyboard handler stays local — ReactFlow doesn't dispatch keyboard
 * events at canvas-level for focused nodes, so Enter/Space MUST be
 * handled here to preserve WCAG 2.1.1.
 */
// ArtifactTileBody's viewer graph reaches services/api (import.meta), so import
// it LAZILY: the collapsed canvas (common case) never pulls the heavy viewer
// bundle, and CRA-jest suites that import this shared node don't crash on
// import.meta at module-load ([[feedback_import_meta_jest_mock_chain]] — lazy
// the api-child). DC-11: memo'd so layout-only churn skips re-render
// (data.content is a stable ref — the canvas content Map sets it once for ok).
const ArtifactTileBodyLazy = React.lazy(() =>
  import('../../../artifacts/ArtifactTileBody').then((m) => ({ default: m.ArtifactTileBody })),
);
const ArtifactTileBodyMemo = React.memo(ArtifactTileBodyLazy);

const ArtifactTileNode: React.FC<NodeProps<ArtifactTileData>> = ({ data }) => {
  const { artifact, onClick, onAsk, askInFlight, askError, spineExpanded, onToggleSpine } = data;
  const { fitView } = useReactFlow();

  // SP-3b/3c — the shared source-spine affordance, rendered in BOTH the collapsed
  // footer band and the expanded header. Three-way (frontend triad precedence):
  //   spine exists (spineExpanded !== undefined) → ⌄/▸ Spine collapse chevron
  //   else a stale askError                       → retryable chip
  //   else                                        → "Ask" (spawn)
  // stopPropagation is REQUIRED: the collapsed tile has no local onClick —
  // activation is canvas-level (RF onNodeClick reads node.data), so a bubbling
  // click would toggle the content viewer. data.spineExpanded (spine-lane) is
  // NOT data.expanded (content viewer) — name-trap.
  const spineControl =
    spineExpanded !== undefined ? (
      // Mirror the blueprint chevron's a11y exactly (BlueprintNode.tsx:410-411):
      // aria-expanded + a state-dependent aria-label so SR users get the toggle
      // state, not a bare "Spine, button".
      <button type="button" data-testid="tile-spine-toggle"
        aria-expanded={spineExpanded}
        aria-label={spineExpanded ? 'Collapse this artifact’s spine' : 'Expand this artifact’s spine'}
        title={spineExpanded ? 'Collapse this artifact’s spine' : 'Expand this artifact’s spine'}
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); onToggleSpine?.(`tile-${artifact.id}`); }}
        className="nodrag nopan inline-flex items-center gap-1 px-2 py-1 rounded-sm text-[11px] font-semibold
                   text-emerald-700 hover:bg-emerald-100
                   dark:text-emerald-300 dark:hover:bg-gray-700">
        <span aria-hidden>{spineExpanded ? '⌄' : '▸'}</span> Spine
      </button>
    ) : askError ? (
      // Retryable — click re-invokes onAsk (the hook clears askError on entry).
      <button type="button" title={`${askError} — click to retry`}
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); onAsk?.(artifact.id); }}
        className="nodrag nopan inline-flex items-center px-1.5 py-0.5 rounded-sm text-[10px] font-semibold
                   bg-rose-100 text-rose-700 hover:bg-rose-200
                   dark:bg-rose-900/60 dark:text-rose-200 dark:hover:bg-rose-900">
        🚫 Retry
      </button>
    ) : onAsk ? (
      <button type="button" disabled={!!askInFlight} data-testid="tile-ask"
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); onAsk(artifact.id); }}
        className="nodrag nopan inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-semibold
                   bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50
                   dark:bg-emerald-500 dark:hover:bg-emerald-400">
        {askInFlight ? 'Asking…' : 'Ask'}
      </button>
    ) : null;

  // SP-3b — F5 survival: a tile whose spine has operator turns default-expands
  // ONCE on mount (mirrors BlueprintNode:93-98) so the conversation isn't hidden
  // behind a collapsed card after a reload. Idempotent set-ADD via the typed
  // seeder; a later manual collapse sticks (ref latches once-per-mount). HARD
  // CONSTRAINT: a tile with NO operator turns stays collapsed (no seedling).
  const operatorSeededRef = useRef(false);
  useEffect(() => {
    if (operatorSeededRef.current || !data.hasOperatorTurns) return;
    operatorSeededRef.current = true;
    data.onSeedSpineExpanded?.(`tile-${artifact.id}`);
  }, [data.hasOperatorTurns, data.onSeedSpineExpanded, artifact.id]);

  const handleKey = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick(artifact.id);
      }
    },
    [artifact.id, onClick],
  );

  // Click-to-activate gating (operator design 2026-06-15). An expanded tile is
  // INERT by default: a transparent shield over the body lets every wheel bubble
  // to ReactFlow's d3-zoom, so the canvas always zooms (no map/mermaid event
  // fighting, never stuck). Clicking the body ACTIVATES it — the shield drops and
  // the inner viewer owns the wheel (map zoom / scroll) while stopPropagation
  // keeps the canvas from fighting. Click outside or press Esc to deactivate.
  const [active, setActive] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Collapsing the tile must reset activation (re-expand starts inert).
  useEffect(() => {
    if (!data.expanded) setActive(false);
  }, [data.expanded]);

  // While active, deactivate on a pointerdown outside this tile or on Esc.
  useEffect(() => {
    if (!active) return;
    const onDocDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setActive(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); setActive(false); }
    };
    // capture so we exit before the canvas-level Esc-collapse-all handler fires
    document.addEventListener('mousedown', onDocDown, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDocDown, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [active]);

  // Trailmap L2 Drip 3 — expanded: blow the tile open to a content-natural
  // card in place. Both hooks above (useReactFlow + handleKey) already ran, so
  // this early return is hooks-safe. Header carries Fit (camera) + collapse ×;
  // body is the chrome-free ArtifactTileBody (memoized) with shimmer/error
  // states. wheel-capture stops inner scroll from panning the canvas (DC-8).
  if (data.expanded) {
    return (
      <>
        <Handle type="target" position={Position.Left} id="l" style={{ opacity: 0 }} />
        <div
          id={`tile-${artifact.id}`}
          // Swallow clicks so they don't bubble to ReactFlow's canvas-level
          // onNodeClick (which routes to handleTileClick → a 2nd toggle that
          // would re-expand on collapse / collapse on a content or Fit click).
          // The × and ⤢ buttons own their actions; collapsed-tile click-to-expand
          // is unaffected (that path has no stopPropagation).
          onClick={(e) => e.stopPropagation()}
          // EXPLICIT pixel footprint = expandedTileSize(type), the SAME box the
          // layout reserves via node.width/height (placeColumns). `h-full` was
          // resolving against the RF node wrapper (no DOM height) → auto → the
          // body grew to full content height (recon 2026-06-14: measured 28,689px)
          // and overflowed onto the tiles below ("artifacts on top of each
          // other"). Pinning height here lets the inner overflow-auto clip+scroll.
          ref={wrapRef}
          data-active={active ? 'true' : 'false'}
          style={{ width: expandedTileSize(artifact.type).w, height: expandedTileSize(artifact.type).h }}
          className={[
            'flex flex-col rounded-lg border-2 bg-white dark:bg-gray-800 shadow-md overflow-hidden',
            active
              ? 'border-emerald-500 ring-2 ring-emerald-400/70 dark:ring-emerald-400/50'
              : 'border-emerald-400 dark:border-emerald-500/80',
          ].join(' ')}
        >
          <div className="flex items-center gap-2 px-3 py-1.5 border-b border-emerald-200 dark:border-emerald-800 text-[12px] font-semibold text-gray-900 dark:text-gray-50">
            <span aria-hidden className="text-emerald-600 dark:text-emerald-400">{_typeGlyphFor(artifact.type)}</span>
            <span className="truncate flex-1">{artifact.name}</span>
            {active && (
              <span
                data-testid="tile-active-hint"
                className="flex-shrink-0 text-[10px] font-medium text-emerald-700 dark:text-emerald-300 normal-case"
                title="You're interacting with this artifact — scroll/zoom stays inside it. Click outside or press Esc to exit."
              >
                interacting · Esc
              </span>
            )}
            {spineControl}
            <button
              type="button"
              data-testid="tile-fit"
              aria-label="Fit camera to this artifact"
              onClick={() => fitView({ nodes: [{ id: `tile-${artifact.id}` }], duration: TILE_FIT_DURATION, padding: TILE_FIT_PADDING, maxZoom: TILE_FIT_MAX_ZOOM })}
              className="px-1.5 text-gray-500 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400"
            >
              ⤢
            </button>
            <button
              type="button"
              data-testid="tile-collapse"
              aria-label="Collapse artifact"
              onClick={() => onClick(artifact.id)}
              className="px-1.5 text-gray-500 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400"
            >
              ×
            </button>
          </div>
          {/* relative wrap hosts the inactive activation shield over the viewer */}
          <div className="relative flex-1 min-h-0">
            <div
              className="absolute inset-0 overflow-auto"
              // Active: inner viewer owns the wheel (map zoom / scroll); stop it so
              // the canvas doesn't fight. Inactive: no handler — the shield blocks
              // inner handling and the wheel bubbles to the canvas zoom.
              onWheelCapture={active ? (e) => e.stopPropagation() : undefined}
            >
              <ArtifactTileErrorBoundary fallback={<RawTextDump artifact={(data.content ?? { content: '' }) as any} />}>
                {data.contentError ? (
                  <ContentLoadFailed artifact={artifact} />
                ) : data.content ? (
                  <React.Suspense fallback={<Shimmer />}>
                    <ArtifactTileBodyMemo artifact={data.content} viewMode="default" />
                  </React.Suspense>
                ) : (
                  <Shimmer />
                )}
              </ArtifactTileErrorBoundary>
            </div>
            {!active && (
              // Transparent activation shield — intercepts pointer + wheel so the
              // inner viewer (MapLibre scrollZoom, mermaid scroll) stays dormant
              // and the wheel bubbles to the canvas. Click to start interacting.
              <div
                data-testid="tile-activate-shield"
                className="absolute inset-0 z-10 cursor-pointer"
                title="Click to interact with this artifact (scroll / zoom inside it)"
                onClick={(e) => { e.stopPropagation(); setActive(true); }}
              />
            )}
          </div>
        </div>
      </>
    );
  }

  const ariaLabel =
    `Artifact ▣: ${artifact.name} (${artifact.type}). Press Enter to open.`;

  // Type → icon glyph. Falls through to ▣ when unmapped — guarantee
  // every tile has a visible marker.
  const typeGlyph = _typeGlyphFor(artifact.type);

  return (
    <>
      <Handle
        type="target"
        position={Position.Left}
        id="l"
        style={{ opacity: 0 }}
      />
      <div
        id={`tile-${artifact.id}`}
        role="button"
        tabIndex={0}
        aria-label={ariaLabel}
        onKeyDown={handleKey}
        className={[
          // Card-shape: two-row layout with breathing room
          'flex flex-col justify-center gap-1.5 px-4 py-3',
          'rounded-lg border-2 shadow-sm w-full h-full',
          // Color: emerald accent on a subtle gradient
          'cursor-pointer select-none',
          'bg-gradient-to-br from-white via-emerald-50/60 to-white',
          'border-emerald-400 text-gray-900',
          'dark:from-gray-800 dark:via-emerald-900/30 dark:to-gray-800',
          'dark:border-emerald-500/80 dark:text-gray-50',
          // Interaction: hover lift + focus ring
          'hover:shadow-md hover:border-emerald-500 transition-shadow',
          'dark:hover:border-emerald-400',
          'focus:outline-none focus:ring-2 focus:ring-emerald-500',
          'focus:ring-offset-1 focus:ring-offset-white',
          'dark:focus:ring-offset-gray-900',
        ].join(' ')}
      >
        {/* Row 1: glyph + name */}
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="text-xl leading-none flex-shrink-0 text-emerald-600 dark:text-emerald-400"
            aria-hidden="true"
          >
            {typeGlyph}
          </span>
          <span className="text-[13px] font-semibold leading-tight truncate flex-1">
            {artifact.name}
          </span>
        </div>
        {/* Row 2: type badge + version + agent (when present) */}
        <div className="flex items-center gap-2 text-[10px] min-w-0">
          <span
            className={[
              'inline-flex items-center px-1.5 py-0.5 rounded-sm font-bold uppercase tracking-wider flex-shrink-0',
              'bg-emerald-100 text-emerald-800',
              'dark:bg-emerald-900/70 dark:text-emerald-100',
            ].join(' ')}
          >
            {artifact.type}
          </span>
          <span
            className="font-mono text-gray-500 dark:text-gray-400 flex-shrink-0"
            title={`Version ${artifact.latest_version}`}
          >
            v{artifact.latest_version}
          </span>
          {artifact.agent_id && (
            <span
              className="text-gray-500 dark:text-gray-400 truncate"
              title={`Created by ${artifact.agent_id}`}
            >
              · {artifact.agent_id}
            </span>
          )}
        </div>
        {/* SP-3b/3c — Row 3: source-spine affordance (Ask → spawn, then ⌄/▸ Spine
            collapse chevron). DIM_H.artifact_tile is 112 to reserve this row. */}
        {spineControl && (
          <div className="flex justify-end pt-1 mt-1 border-t border-emerald-200/60 dark:border-emerald-700/40">
            {spineControl}
          </div>
        )}
      </div>
    </>
  );
};


// Map artifact_type → leading glyph. Falls back to ▣ for unknown
// types so every tile has a visible marker. Glyphs picked to be
// stylistically consistent (single-emoji or geometric shape) and
// to compress to legibility at small sizes.
function _typeGlyphFor(type: string): string {
  switch (type) {
    case 'document':
    case 'markdown':
    case 'text':
      return '📄';
    case 'code':
      return '💻';
    case 'image':
      return '🖼️';
    case 'idea':
      return '💡';
    case 'critique':
      return '🔍';
    case 'solution':
      return '✅';
    case 'diagram':
    case 'mermaid':
    case 'mindmap':
      return '🧜';
    case 'json':
    case 'yaml':
    case 'toml':
    case 'csv':
      return '📊';
    case 'prompt':
      return '💬';
    case 'decision_log':
      return '⚖️';
    case 'pulse_digest':
      return '📰';
    case 'handoff_note':
      return '🤝';
    case 'project_manifest':
      return '📋';
    case 'persona_chat':
      return '💬';
    case 'geojson':
      return '🗺️';
    default:
      return '▣';
  }
}

export default ArtifactTileNode;
